import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, FlatList, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, Image} from 'react-native';
import {
  Text, TextInput, Button, Chip, Divider, ActivityIndicator, Snackbar,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {measurementApi} from '../../services/api/measurementApi';
import {customerApi} from '../../services/api/customerApi';
import {colors, spacing} from '../../theme';

const MeasurementFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId} = route.params;
  const preselectedCatalogItemId = route.params?.catalogItemId;
  const editProfileId = route.params?.profileId;
  const returnTo = route.params?.returnTo;
  const paramMode = route.params?.mode;
  const originalProfileName = route.params?.originalProfileName;

  const {catalogItems, fetchCatalogItems, createProfile, updateProfile} = useMeasurementStore();

  const isEditMode = paramMode === 'edit' || !!editProfileId;
  const [step, setStep] = useState(preselectedCatalogItemId || isEditMode ? 2 : 1);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const [measurementOptions, setMeasurementOptions] = useState([]);
  const [values, setValues] = useState({});
  const [profileName, setProfileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingFields, setLoadingFields] = useState(isEditMode);
  const [snackbar, setSnackbar] = useState('');
  const [customerGender, setCustomerGender] = useState(route.params?.customerGender || null);

  const {fetchByType, getProperty} = useConfigStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();
  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;

  useEffect(() => {
    if (orgUnits.length === 0) fetchOrgUnits();
  }, [orgUnits.length, fetchOrgUnits]);

  useEffect(() => {
    if (effectiveOrgUnitId) fetchByType('ORDER', effectiveOrgUnitId);
  }, [effectiveOrgUnitId, fetchByType]);

  const measurementUnit = getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES';
  const unitLabel = measurementUnit === 'CMS' ? 'cms' : 'in';

  useEffect(() => {
    if (!isEditMode) fetchCatalogItems();
  }, [isEditMode, fetchCatalogItems]);

  useEffect(() => {
    if (!customerGender && customerId) {
      customerApi.getById(customerId).then(res => {
        setCustomerGender(res.data.data?.gender || null);
      }).catch(() => {});
    }
  }, [customerId, customerGender]);

  const optionKey = (item) =>
    item.globalOptionId ? String(item.globalOptionId) : 'custom_' + item.customOptionId;

  useEffect(() => {
    if (!isEditMode || !editProfileId) return;

    const loadProfile = async () => {
      setLoadingFields(true);
      try {
        const profileRes = await measurementApi.getProfile(customerId, editProfileId);
        const profile = profileRes.data.data;

        setProfileName(profile.profileName || '');

        const dtId = profile.catalogItemId || preselectedCatalogItemId;

        const catalogItemRes = await measurementApi.getCatalogItem(dtId);
        const catalogItem = catalogItemRes.data.data;
        if (catalogItem) setSelectedCatalogItem(catalogItem);

        const mvRes = await measurementApi.getMeasurementValues(dtId);
        const options = (mvRes.data.data || []).filter(item => item.isEnabled);
        setMeasurementOptions(options);

        const prefilled = options.reduce((acc, item) => ({...acc, [optionKey(item)]: ''}), {});
        if (profile.values) {
          profile.values.forEach(v => {
            const key = v.globalOptionId ? String(v.globalOptionId) : v.customOptionId ? 'custom_' + v.customOptionId : null;
            if (key && prefilled.hasOwnProperty(key)) {
              prefilled[key] = v.value?.toString() || '';
            }
          });
        }
        setValues(prefilled);
      } catch (e) {
        setSnackbar(e.response?.data?.error?.message || t('common.error'));
      } finally {
        setLoadingFields(false);
      }
    };
    loadProfile();
  }, [isEditMode, editProfileId, customerId, preselectedCatalogItemId, t]);

  useEffect(() => {
    if (!isEditMode && preselectedCatalogItemId && catalogItems.length > 0) {
      const dt = catalogItems.find(d => d.id === preselectedCatalogItemId);
      if (dt) {
        selectCatalogItem(dt);
      }
    }
  }, [isEditMode, preselectedCatalogItemId, catalogItems]);

  const selectCatalogItem = useCallback(async dt => {
    setSelectedCatalogItem(dt);
    setLoadingFields(true);
    try {
      const mvRes = await measurementApi.getMeasurementValues(dt.id);
      const options = (mvRes.data.data || []).filter(item => item.isEnabled);
      setMeasurementOptions(options);
      setValues(options.reduce((acc, item) => ({...acc, [optionKey(item)]: ''}), {}));
      setProfileName(`${dt.name} - ${new Date().toLocaleDateString()}`);
      setStep(2);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setLoadingFields(false);
    }
  }, [t]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const measurementValues = Object.entries(values)
        .filter(([_, v]) => v.toString().trim() !== '')
        .map(([key, value]) => {
          if (key.startsWith('custom_')) {
            return {customOptionId: Number(key.replace('custom_', '')), value: value.toString().trim()};
          }
          return {globalOptionId: Number(key), value: value.toString().trim()};
        });

      const payload = {
        catalogItemId: selectedCatalogItem.id,
        profileName,
        measuredAt: new Date().toISOString().split('T')[0],
        values: measurementValues,
      };

      let savedProfile;
      if (isEditMode && originalProfileName && profileName === originalProfileName) {
        savedProfile = await updateProfile(customerId, editProfileId, payload);
      } else if (isEditMode && originalProfileName && profileName !== originalProfileName) {
        savedProfile = await createProfile(customerId, payload);
      } else if (isEditMode) {
        savedProfile = await updateProfile(customerId, editProfileId, payload);
      } else {
        savedProfile = await createProfile(customerId, payload);
      }

      if (returnTo === 'OrderLineDetail' && savedProfile) {
        navigation.popTo('OrderLineDetail', {
          measurementProfileId: savedProfile.id,
          measurementProfileName: savedProfile.profileName,
        }, { merge: true });
      } else {
        navigation.goBack();
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [values, selectedCatalogItem, profileName, customerId, editProfileId, isEditMode, originalProfileName, returnTo, createProfile, updateProfile, navigation, t]);

  /* ── Step 1: Catalog item picker ── */
  const renderCatalogItem = ({item}) => (
    <TouchableOpacity
      onPress={() => selectCatalogItem(item)}
      activeOpacity={0.7}
      style={styles.catalogCard}>
      {item.mainImageUrl ? (
        <Image source={{uri: item.mainImageUrl}} style={styles.catalogImage} />
      ) : (
        <View style={styles.catalogImageEmpty}>
          <Icon name="tshirt-crew-outline" size={22} color={colors.primary} />
        </View>
      )}
      <View style={styles.catalogInfo}>
        <Text style={styles.catalogName}>{item.name}</Text>
        {item.description && (
          <Text style={styles.catalogDesc} numberOfLines={1}>{item.description}</Text>
        )}
      </View>
      <Icon name="chevron-right" size={22} color={colors.textLight} />
    </TouchableOpacity>
  );

  const showStepper = !isEditMode && !preselectedCatalogItemId;

  return (
    <View style={styles.container}>
      <GradientHeader
        title={isEditMode ? t('measurement.editMeasurement') : t('measurement.title')}
        onBack={() => step > 1 && !preselectedCatalogItemId && !isEditMode ? setStep(1) : navigation.goBack()}
      />

      {showStepper && (
        <View style={styles.stepperBar}>
          <TouchableOpacity
            style={styles.stepItem}
            activeOpacity={0.7}
            onPress={() => step > 1 && setStep(1)}>
            <View style={[styles.stepCircle, step >= 1 && styles.stepCircleActive]}>
              {step > 1 ? (
                <Icon name="check" size={14} color={colors.white} />
              ) : (
                <Text style={[styles.stepNumber, step >= 1 && styles.stepNumberActive]}>1</Text>
              )}
            </View>
            <Text style={[styles.stepLabel, step >= 1 && styles.stepLabelActive]}>
              {step > 1 && selectedCatalogItem ? selectedCatalogItem.name : 'Select Dress'}
            </Text>
          </TouchableOpacity>
          <View style={[styles.stepLine, step > 1 && styles.stepLineActive]} />
          <View style={styles.stepItem}>
            <View style={[styles.stepCircle, step >= 2 && styles.stepCircleActive]}>
              <Text style={[styles.stepNumber, step >= 2 && styles.stepNumberActive]}>2</Text>
            </View>
            <Text style={[styles.stepLabel, step >= 2 && styles.stepLabelActive]}>Measurements</Text>
          </View>
        </View>
      )}

      {step === 1 && (
        catalogItems.length === 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <FlatList
            data={catalogItems}
            keyExtractor={item => String(item.id)}
            renderItem={renderCatalogItem}
            contentContainerStyle={styles.catalogList}
            ListHeaderComponent={
              <Text style={styles.stepHint}>Select a dress type to measure</Text>
            }
          />
        )
      )}

      {step === 2 && (
      <>
      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {loadingFields ? (
          <ActivityIndicator size="large" color={colors.primary} style={{marginTop: spacing.xxl}} />
        ) : (
          <>
            {/* Selected catalog item banner */}
            <View style={styles.selectedBanner}>
              {selectedCatalogItem?.mainImageUrl ? (
                <Image source={{uri: selectedCatalogItem.mainImageUrl}} style={styles.selectedImage} />
              ) : (
                <View style={styles.selectedImageEmpty}>
                  <Icon name="tshirt-crew-outline" size={18} color={colors.primary} />
                </View>
              )}
              <Text style={styles.selectedName}>{selectedCatalogItem?.name}</Text>
            </View>

            <View style={styles.unitBanner}>
              <Icon name="ruler" size={16} color={colors.primary} />
              <Text style={styles.unitBannerText}>
                All measurements in {measurementUnit === 'CMS' ? 'Centimeters (cms)' : 'Inches (in)'}
              </Text>
            </View>

            {/* Measurement rows */}
            {measurementOptions.map((item, idx) => {
              const imgUrl = customerGender === 'MALE' ? item.imageUrl1 : item.imageUrl2;
              return (
                <View key={optionKey(item)} style={styles.measurementCard}>
                  <View style={styles.cardTop}>
                    <View style={styles.serialBadge}>
                      <Text style={styles.serialText}>{idx + 1}</Text>
                    </View>
                    <Text style={styles.measurementName}>
                      {item.globalOptionName || item.customOptionName}
                    </Text>
                  </View>
                  <View style={styles.cardBottom}>
                    {imgUrl ? (
                      <Image source={{uri: imgUrl}} style={styles.measurementImage} />
                    ) : (
                      <View style={styles.measurementImageEmpty}>
                        <Icon name="human-male-height-variant" size={28} color={colors.textLight} />
                      </View>
                    )}
                    <TextInput
                      label={`${item.globalOptionName || item.customOptionName} (${unitLabel})`}
                      value={values[optionKey(item)]?.toString() || ''}
                      onChangeText={v => setValues(prev => ({...prev, [optionKey(item)]: v}))}
                      mode="outlined"
                      style={styles.measurementInput}
                      keyboardType="numeric"
                      outlineColor={colors.border}
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </View>
              );
            })}

            {/* Profile name at the bottom before save */}
            <View style={styles.profileNameSection}>
              <View style={styles.profileNameTitleRow}>
                <Icon name="tag-outline" size={18} color={colors.primary} />
                <Text style={styles.profileNameTitle}>{t('measurement.profileName')}</Text>
              </View>
              <TextInput
                value={profileName}
                onChangeText={setProfileName}
                mode="outlined"
                style={styles.profileNameInput}
                placeholder="e.g. Shirt - March 2026"
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            </View>

            <View style={{height: spacing.xxl}} />
          </>
        )}
      </ScrollView>
      </KeyboardAvoidingView>

      {/* Sticky save button */}
      <View style={styles.saveBar}>
        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}
          buttonColor={colors.primary}>
          {returnTo ? 'Save & Continue' : t('common.save')}
        </Button>
      </View>
      </>
      )}

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  content: {flex: 1, padding: spacing.md},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},

  // Stepper
  stepperBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  stepItem: {
    alignItems: 'center',
    gap: 4,
  },
  stepCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepCircleActive: {
    backgroundColor: colors.primary,
  },
  stepNumber: {fontSize: 13, fontWeight: '700', color: colors.textSecondary},
  stepNumberActive: {color: colors.white},
  stepLabel: {fontSize: 11, fontWeight: '500', color: colors.textSecondary},
  stepLabelActive: {color: colors.primary, fontWeight: '600'},
  stepLine: {
    flex: 1,
    height: 2,
    backgroundColor: colors.border,
    marginHorizontal: spacing.sm,
    marginBottom: 18,
  },
  stepLineActive: {
    backgroundColor: colors.primary,
  },

  // Step 1: Catalog picker
  catalogList: {padding: spacing.sm, paddingBottom: spacing.xxl, gap: spacing.sm},
  stepHint: {
    fontSize: 13, color: colors.textSecondary, fontWeight: '500',
    paddingHorizontal: spacing.sm, marginBottom: spacing.xs,
  },
  catalogCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    gap: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  catalogImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.background,
  },
  catalogImageEmpty: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary + '12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  catalogInfo: {flex: 1},
  catalogName: {fontSize: 15, fontWeight: '600', color: colors.text},
  catalogDesc: {fontSize: 12, color: colors.textSecondary, marginTop: 2},

  // Step 2: Selected item banner
  selectedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary + '12',
    borderRadius: 10,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  selectedImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.background,
  },
  selectedImageEmpty: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary + '20',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedName: {fontSize: 14, fontWeight: '600', color: colors.primary},

  // Unit banner
  unitBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.surface,
    borderRadius: 8,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  unitBannerText: {fontSize: 13, fontWeight: '600', color: colors.primary},

  // Measurement card
  measurementCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  serialBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  serialText: {fontSize: 12, fontWeight: '700', color: colors.primary},
  measurementName: {fontSize: 14, fontWeight: '600', color: colors.text, flex: 1},
  cardBottom: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  measurementImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: colors.background,
  },
  measurementImageEmpty: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: colors.background,
    justifyContent: 'center',
    alignItems: 'center',
  },
  measurementInput: {
    flex: 1,
    backgroundColor: colors.surface,
  },

  // Profile name section
  profileNameSection: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.md,
    marginTop: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
  },
  profileNameTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  profileNameTitle: {fontSize: 14, fontWeight: '600', color: colors.primary},
  profileNameInput: {backgroundColor: colors.surface},

  // Sticky save
  saveBar: {
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.divider,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 4,
  },
  saveButton: {borderRadius: 8},
});

export default MeasurementFormScreen;
